/**
 * Self-service notification opt-out modal (any winery user).
 */
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Toggle } from './ui';
import MobileModal from './MobileModal';

interface NotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationPreferencesModal: React.FC<NotificationPreferencesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [selfOptOut, setSelfOptOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);
        const { data } = await api.get('/api/notifications/me');
        if (!cancelled) {
          setSelfOptOut(Boolean(data.selfOptOut));
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load notification preferences.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/api/notifications/me', { selfOptOut });
      setSuccess(true);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Unable to save preferences.';
      setError(typeof message === 'string' ? message : 'Unable to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileModal isOpen={isOpen} onClose={onClose} title="Email notifications" size="md">
      <div className="space-y-4 p-1">
        {loading ? (
          <p className="text-sm text-monday-text-muted">Loading…</p>
        ) : (
          <>
            <Toggle
              label="Opt out of winery emails"
              description="When enabled, you will not receive operational notification emails for this winery. Admins may still see you as opted out in the Notification Center."
              checked={selfOptOut}
              onChange={(e) => {
                setSelfOptOut(e.target.checked);
                setSuccess(false);
              }}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            {success && (
              <p className="text-sm text-green-700">Preferences saved.</p>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button
                type="button"
                className="mobile-btn mobile-btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="mobile-btn mobile-btn-primary"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </MobileModal>
  );
};

export default NotificationPreferencesModal;
